import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Form, Tabs, Tab } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function BrandVoiceAnalysis() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('text');

  // Input states
  const [textInput, setTextInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [existingDescription, setExistingDescription] = useState('');

  // Analysis result
  const [voiceAnalysis, setVoiceAnalysis] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedAnalysis, setEditedAnalysis] = useState('');
  const [saving, setSaving] = useState(false);

  // Voice calibration for IP-level content
  const [voiceExamplesLove, setVoiceExamplesLove] = useState('');
  const [voiceExamplesHate, setVoiceExamplesHate] = useState('');
  const [provocativePreference, setProvocativePreference] = useState('moderate');

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load saved voice description if it exists
    await fetchSavedVoiceDescription();

    setLoading(false);
  };

  const fetchSavedVoiceDescription = async () => {
    try {
      const response = await fetch('/api/brand/get-saved-voice-description', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setVoiceAnalysis(data.voice_description);
        setEditedAnalysis(data.voice_description);
        // Load voice calibration data
        if (data.voice_examples_love) setVoiceExamplesLove(data.voice_examples_love);
        if (data.voice_examples_hate) setVoiceExamplesHate(data.voice_examples_hate);
        if (data.provocative_preference) setProvocativePreference(data.provocative_preference);
      }
    } catch (err) {
      console.log('No saved voice description found or error fetching:', err);
    }
  };

  const handleAnalyze = async () => {
    let content = '';
    let inputType = '';

    if (activeTab === 'text') {
      if (!textInput.trim()) {
        setError('Please paste some writing samples');
        setTimeout(() => setError(''), 3000);
        return;
      }
      content = textInput;
      inputType = 'text';
    } else if (activeTab === 'url') {
      if (!urlInput.trim()) {
        setError('Please enter a URL');
        setTimeout(() => setError(''), 3000);
        return;
      }
      content = urlInput;
      inputType = 'url';
    } else if (activeTab === 'existing') {
      if (!existingDescription.trim()) {
        setError('Please paste your voice description');
        setTimeout(() => setError(''), 3000);
        return;
      }
      content = existingDescription;
      inputType = 'existing_description';
    }

    setAnalyzing(true);
    setError('');

    try {
      const response = await fetch('/api/brand/analyze-voice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ input_type: inputType, content: content })
      });

      if (response.ok) {
        const data = await response.json();
        setVoiceAnalysis(data.voice_analysis);
        setEditedAnalysis(data.voice_analysis);
        setIsEditing(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to analyze voice');
      }
    } catch (err) {
      setError('Error analyzing voice: ' + err.message);
    }

    setAnalyzing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/brand/save-voice-description', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          voice_description: isEditing ? editedAnalysis : voiceAnalysis,
          voice_examples_love: voiceExamplesLove,
          voice_examples_hate: voiceExamplesHate,
          provocative_preference: provocativePreference
        })
      });

      if (response.ok) {
        navigate('/visual-mood-board');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save voice description');
      }
    } catch (err) {
      setError('Error saving voice description: ' + err.message);
    }

    setSaving(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedAnalysis(voiceAnalysis);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedAnalysis(voiceAnalysis);
  };

  const handleSaveEdit = () => {
    setVoiceAnalysis(editedAnalysis);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-3">Brand Voice Analysis</h1>
              <p className="text-center text-muted mb-4">
                Analyze your writing samples to create a comprehensive voice description that will guide AI content generation.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}

              {!voiceAnalysis ? (
                <>
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-4"
                  >
                    <Tab eventKey="text" title="📝 Paste Text">
                      <div className="mt-4">
                        <Form.Group>
                          <Form.Label><strong>Paste Writing Samples</strong></Form.Label>
                          <Form.Text className="d-block mb-2">
                            Paste 1-3 examples of your writing (blog posts, emails, social media posts, etc.)
                          </Form.Text>
                          <Form.Control
                            as="textarea"
                            rows={12}
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                            placeholder="Paste your writing samples here..."
                          />
                        </Form.Group>
                      </div>
                    </Tab>

                    <Tab eventKey="url" title="🔗 Paste URL">
                      <div className="mt-4">
                        <Form.Group>
                          <Form.Label><strong>Blog Post or Article URL</strong></Form.Label>
                          <Form.Text className="d-block mb-2">
                            Enter the URL of a blog post, article, or other published writing
                          </Form.Text>
                          <Form.Control
                            type="text"
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                            placeholder="https://yourblog.com/article"
                          />
                        </Form.Group>
                        <Alert variant="info" className="mt-3">
                          <small>
                            <strong>Note:</strong> For best results, paste multiple URLs separated by commas,
                            or use the text tab to paste content from multiple sources.
                          </small>
                        </Alert>
                      </div>
                    </Tab>

                    <Tab eventKey="existing" title="✍️ Paste Voice Description">
                      <div className="mt-4">
                        <Form.Group>
                          <Form.Label><strong>Existing Voice Description</strong></Form.Label>
                          <Form.Text className="d-block mb-2">
                            If you already have a voice description from AI or elsewhere, paste it here
                          </Form.Text>
                          <Form.Control
                            as="textarea"
                            rows={12}
                            value={existingDescription}
                            onChange={(e) => setExistingDescription(e.target.value)}
                            placeholder="Paste your voice description here..."
                          />
                        </Form.Group>
                      </div>
                    </Tab>
                  </Tabs>

                  <div className="text-center mt-4">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleAnalyze}
                      disabled={analyzing}
                    >
                      {analyzing ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {activeTab === 'existing' ? 'Processing...' : 'Analyzing Voice...'}
                        </>
                      ) : (
                        activeTab === 'existing' ? 'Use This Description →' : 'Analyze My Voice →'
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Alert variant="success" className="text-center mb-4">
                    <strong>✓ Voice Analysis Complete</strong>
                  </Alert>

                  {!isEditing ? (
                    <Card className="mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                      <Card.Body>
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {voiceAnalysis}
                        </div>
                      </Card.Body>
                    </Card>
                  ) : (
                    <Form.Group className="mb-4">
                      <Form.Label><strong>Edit Your Voice Description</strong></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={15}
                        value={editedAnalysis}
                        onChange={(e) => setEditedAnalysis(e.target.value)}
                      />
                    </Form.Group>
                  )}

                  {/* Voice Calibration for IP-Level Content */}
                  <Card className="mb-4 border-primary">
                    <Card.Header className="bg-primary text-white">
                      <strong>Voice Calibration</strong>
                      <small className="d-block">Help AI generate content that matches your style</small>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-4">
                        <Form.Label><strong>Content I Love Sounds Like...</strong></Form.Label>
                        <Form.Text className="d-block mb-2">
                          Paste examples or describe the tone/style of content you admire
                        </Form.Text>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={voiceExamplesLove}
                          onChange={(e) => setVoiceExamplesLove(e.target.value)}
                          placeholder="Example: Bold opening statements that challenge assumptions. Historical parallels that show deep thinking. No fluff or filler..."
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label><strong>Content I Hate Sounds Like...</strong></Form.Label>
                        <Form.Text className="d-block mb-2">
                          Describe the style/tone you want to avoid
                        </Form.Text>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={voiceExamplesHate}
                          onChange={(e) => setVoiceExamplesHate(e.target.value)}
                          placeholder="Example: Generic listicles with no depth. Overly casual 'hey friend' tone. Clickbait that doesn't deliver..."
                        />
                      </Form.Group>

                      <Form.Group>
                        <Form.Label><strong>How Provocative Should Your Content Be?</strong></Form.Label>
                        <Form.Text className="d-block mb-2">
                          How edgy or challenging do you want your content?
                        </Form.Text>
                        <div className="d-flex gap-3">
                          <Form.Check
                            type="radio"
                            id="prov-safe"
                            name="provocative"
                            label="Safe - Conventional wisdom, widely accepted"
                            checked={provocativePreference === 'safe'}
                            onChange={() => setProvocativePreference('safe')}
                          />
                          <Form.Check
                            type="radio"
                            id="prov-moderate"
                            name="provocative"
                            label="Moderate - Fresh perspectives, mild challenges"
                            checked={provocativePreference === 'moderate'}
                            onChange={() => setProvocativePreference('moderate')}
                          />
                          <Form.Check
                            type="radio"
                            id="prov-bold"
                            name="provocative"
                            label="Bold - Contrarian, comfortable making people uncomfortable"
                            checked={provocativePreference === 'bold'}
                            onChange={() => setProvocativePreference('bold')}
                          />
                        </div>
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  <div className="d-flex justify-content-between mt-4">
                    <div>
                      <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/dashboard/branding')}
                        className="me-2"
                      >
                        ← Back to Dashboard
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setVoiceAnalysis('');
                          setEditedAnalysis('');
                          setIsEditing(false);
                          setTextInput('');
                          setUrlInput('');
                          setExistingDescription('');
                        }}
                        className="me-2"
                      >
                        ↻ Start Over
                      </Button>
                      {!isEditing ? (
                        <Button
                          variant="outline-primary"
                          onClick={handleEdit}
                        >
                          ✏️ Edit
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline-secondary"
                            onClick={handleCancelEdit}
                            className="me-2"
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            onClick={handleSaveEdit}
                          >
                            Save Changes
                          </Button>
                        </>
                      )}
                    </div>
                    <Button
                      variant="success"
                      size="lg"
                      disabled={saving || isEditing}
                      onClick={handleSave}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save & Continue →'
                      )}
                    </Button>
                  </div>
                </>
              )}

              {!voiceAnalysis && (
                <div className="text-center mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/branding')}
                  >
                    ← Back to Dashboard
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default BrandVoiceAnalysis;
